import { useMemo } from "react";
import { hexToRgba, themeFor } from "@/lib/menuTheme";

const rng = (seed) => () => {
  seed |= 0;
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};
export const hashStr = (s) => [...s].reduce((h, c) => (Math.imul(31, h) + c.charCodeAt(0)) | 0, 7);

const flowPath = (r, y0, w, h, amp) => {
  const pts = [];
  const f1 = 1 + r() * 2, f2 = 2 + r() * 3, ph = r() * 6;
  for (let x = -20; x <= w + 20; x += 20) {
    const y = y0 + Math.sin(x / w * Math.PI * f1 + ph) * amp + Math.sin(x / w * Math.PI * f2 + ph * 2) * amp * 0.35;
    pts.push(`${x},${y.toFixed(1)}`);
  }
  return "M" + pts.join(" L");
};

export const Contours = ({ className, color = "#0F8A96", seed = 1, lines = 12, w = 800, h = 600, strokeWidth = 1 }) => {
  const paths = useMemo(() => {
    const r = rng(seed);
    return Array.from({ length: lines }, (_, i) => flowPath(r, (h / (lines + 1)) * (i + 1), w, h, 18 + r() * 30));
  }, [seed, lines, w, h]);
  return (
    <svg className={className} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid slice" fill="none" stroke={color} strokeWidth={strokeWidth}>
      {paths.map((d, i) => <path key={i} d={d} opacity={0.35 + (i % 3) * 0.2} vectorEffect="non-scaling-stroke" />)}
    </svg>
  );
};

const motifShapes = (type, r) => {
  const out = [];
  if (type === "silk") for (let i = 0; i < 9; i++) out.push(<path key={i} d={flowPath(r, 60 + i * 22, 400, 300, 26)} />);
  if (type === "rings") [[300, 90], [110, 220]].forEach(([cx, cy], k) => { for (let i = 1; i < 7; i++) out.push(<circle key={`${k}${i}`} cx={cx} cy={cy} r={i * 16} />); });
  if (type === "hex") for (let row = 0; row < 7; row++) for (let col = 0; col < 9; col++) {
    const cx = col * 52 + (row % 2) * 26, cy = row * 45, s = 26;
    const d = Array.from({ length: 6 }, (_, k) => { const a = (Math.PI / 3) * k + Math.PI / 6; return `${(cx + s * Math.cos(a)).toFixed(1)},${(cy + s * Math.sin(a)).toFixed(1)}`; });
    out.push(<polygon key={`${row}-${col}`} points={d.join(" ")} />);
  }
  if (type === "tide") for (let i = 0; i < 14; i++) out.push(<path key={i} d={flowPath(r, 20 + i * 20, 400, 300, 5)} />);
  if (type === "contour") { const cx = 250 + r() * 80, cy = 140 + r() * 40; for (let i = 1; i < 9; i++) {
    const pts = Array.from({ length: 24 }, (_, k) => { const a = (k / 24) * Math.PI * 2; const rr = i * 17 * (1 + 0.12 * Math.sin(a * 3 + i)); return `${(cx + rr * Math.cos(a)).toFixed(1)},${(cy + rr * Math.sin(a) * 0.8).toFixed(1)}`; });
    out.push(<polygon key={i} points={pts.join(" ")} />); } }
  if (type === "veins") { out.push(<path key="s" d="M40 280 C 160 200, 240 140, 380 20" />); for (let i = 1; i < 9; i++) { const t = i / 9, x = 40 + 340 * t, y = 280 - 260 * t; out.push(<path key={`a${i}`} d={`M${x} ${y} q ${-30 - i * 4} -10 ${-60 - i * 5} -50`} />); out.push(<path key={`b${i}`} d={`M${x} ${y} q 10 ${30 + i * 2} 60 ${40 + i * 3}`} />); } }
  if (type === "harmonic") for (let i = 0; i < 8; i++) { const pts = []; for (let x = 0; x <= 400; x += 8) pts.push(`${x},${(40 + i * 30 + Math.sin(x / 400 * Math.PI * (2 + i)) * 10).toFixed(1)}`); out.push(<path key={i} d={"M" + pts.join(" L")} />); }
  if (type === "moon") { out.push(<path key="m" d="M300 40 a70 70 0 1 0 40 120 a56 56 0 1 1 -40 -120z" />); for (let i = 1; i < 8; i++) out.push(<path key={i} d={`M ${20} ${300 - i * 10} Q 200 ${200 - i * 22}, 400 ${300 - i * 14}`} />); }
  if (type === "spiral") { const pts = []; for (let a = 0; a < Math.PI * 12; a += 0.15) { const rr = 4 + a * 5.2; pts.push(`${(280 + rr * Math.cos(a)).toFixed(1)},${(150 + rr * Math.sin(a) * 0.85).toFixed(1)}`); } out.push(<path key="sp" d={"M" + pts.join(" L")} />); }
  return out;
};

export const Motif = ({ menuKey, className, opacity = 0.5, seed = 3 }) => {
  const t = themeFor(menuKey);
  const shapes = useMemo(() => motifShapes(t.motif, rng(seed)), [t.motif, seed]);
  return (
    <svg className={className} viewBox="0 0 400 300" preserveAspectRatio="xMidYMid slice" fill="none" stroke={t.glow} strokeWidth="0.8" opacity={opacity} aria-hidden>
      {shapes}
    </svg>
  );
};

export const DishArtwork = ({ menuKey, name, testId, className = "" }) => {
  const t = themeFor(menuKey);
  return (
    <div data-testid={testId} className={`relative h-full w-full overflow-hidden ${className}`} style={{ background: `radial-gradient(80% 90% at 70% 40%, ${hexToRgba(t.glow, 0.22)} 0%, rgba(255,255,255,0) 70%)` }}>
      <svg aria-hidden viewBox="0 0 120 80" className="absolute left-1/2 top-1/2 h-24 w-36 -translate-x-1/2 -translate-y-1/2" fill="none" stroke={t.glow} strokeOpacity="0.35" strokeWidth="1.2">
        <ellipse cx="60" cy="44" rx="46" ry="16" /><ellipse cx="60" cy="42" rx="30" ry="9" /><path d="M14 44c0 10 20 18 46 18s46-8 46-18" />
      </svg>
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-white/70 to-transparent" />
    </div>
  );
};
